// Suggested Path: src/features/olsme/constants/subscriptionData.ts

export interface AgeTierPricing {
  regular: number;
  promo: number | null; // Promo can be optional
}

export interface LocationPricing {
  "1000": AgeTierPricing;
  "10000": AgeTierPricing;
  "100000": AgeTierPricing;
  "1000000": AgeTierPricing;
  "10000000": AgeTierPricing;
  [key: string]: AgeTierPricing; // For dynamic access
}

export interface SubscriptionAgeTier extends LocationPricing {
  ageRange: { min: number; max: number };
  ageDisplay: string; // For display in tables like in olsmeSub
  percentage?: number | null; // For the '%' column in olsmeSub
}

// Using data structure from olsme.tsx as base, adding ageDisplay and placeholder for percentage
// YOU MUST VERIFY THIS DATA, ESPECIALLY FOR AGES 31+ and THE '%' COLUMN.
// The original olsmeSub.tsx had very different prices for 31+
export const OLSME_SUBSCRIPTION_DATA: SubscriptionAgeTier[] = [
  {
    ageRange: { min: 0, max: 14 },
    ageDisplay: "0-14 (with p.)",
    "1000": { regular: 21.60, promo: 17.71 },
    "10000": { regular: 25.20, promo: 20.66 },
    "100000": { regular: 27.60, promo: 22.63 },
    "1000000": { regular: 32.40, promo: 26.57 },
    "10000000": { regular: 42.00, promo: 37.03 },
    percentage: 18, // from olsmeSub data
  },
  {
    ageRange: { min: 15, max: 21 },
    ageDisplay: "15-21",
    "1000": { regular: 26.40, promo: 23.23 },
    "10000": { regular: 31.20, promo: 27.46 },
    "100000": { regular: 33.60, promo: 29.57 },
    "1000000": { regular: 42.00, promo: 37.00 },
    "10000000": { regular: 54.00, promo: 49.97 },
    percentage: 12, // from olsmeSub data
  },
  {
    ageRange: { min: 22, max: 30 },
    ageDisplay: "22-30",
    "1000": { regular: 31.20, promo: 28.02 }, // olsme.tsx data, olsmeSub had 29.02
    "10000": { regular: 35.00, promo: 31.00 }, // olsme.tsx data, olsmeSub had 37.00, 35.00
    "100000": { regular: 39.20, promo: 35.18 },// olsme.tsx data, olsmeSub had 43.20, 40.18
    "1000000": { regular: 44.40, promo: 39.87 },// olsme.tsx data, olsmeSub had 50.40, 46.87
    "10000000": { regular: 49.00, promo: 52.98 },// olsme.tsx data, olsmeSub had 66.00, 62.98 -> Check this, seems promo is higher
    percentage: 7,   // from olsmeSub data
  },
  {
    ageRange: { min: 31, max: 40 },
    ageDisplay: "31-40",
    "1000": { regular: 33.10, promo: 31.00 }, // Using olsme.tsx data. olsmeSub had 3.10, null
    "10000": { regular: 38.70, promo: 34.00 },// Using olsme.tsx data. olsmeSub had 3.70, null
    "100000": { regular: 43.40, promo: 39.00 },// Using olsme.tsx data. olsmeSub had 4.40, null
    "1000000": { regular: 49.00, promo: 45.00 },// Using olsme.tsx data. olsmeSub had 5.00, null
    "10000000": { regular: 54.50, promo: 49.00 },// Using olsme.tsx data. olsmeSub had 6.50, null
    percentage: null, // from olsmeSub data
  },
  {
    ageRange: { min: 41, max: 50 },
    ageDisplay: "41-50",
    "1000": { regular: 35.60, promo: 33.00 },
    "10000": { regular: 40.20, promo: 36.00 },
    "100000": { regular: 46.00, promo: 42.00 },
    "1000000": { regular: 51.00, promo: 46.00 },
    "10000000": { regular: 56.50, promo: 50.00 },
    percentage: null,
  },
  {
    ageRange: { min: 51, max: 60 },
    ageDisplay: "51-60",
    "1000": { regular: 39.20, promo: 37.00 },
    "10000": { regular: 44.80, promo: 39.00 },
    "100000": { regular: 50.70, promo: 44.00 },
    "1000000": { regular: 55.70, promo: 49.00 },
    "10000000": { regular: 60.50, promo: 55.00 },
    percentage: null,
  },
  {
    ageRange: { min: 61, max: Infinity },
    ageDisplay: "61+",
    "1000": { regular: 41.80, promo: 39.00 },
    "10000": { regular: 46.70, promo: 42.00 },
    "100000": { regular: 50.60, promo: 46.00 },
    "1000000": { regular: 58.70, promo: 51.00 },
    "10000000": { regular: 64.50, promo: 56.00 },
    percentage: null,
  },
];

export interface CalculatedPrices {
  subscriptionPrice: number | null;
  promoPrice: number | null;
  projectedPrice: number | null; // 2026 projected monthly
}

export const calculateOlsmePrices = (
  userAge: number | null,
  locationSize: string | null,
  data: SubscriptionAgeTier[] = OLSME_SUBSCRIPTION_DATA
): CalculatedPrices => {
  if (userAge === null || locationSize === null) {
    return { subscriptionPrice: null, promoPrice: null, projectedPrice: null };
  }

  const ageTier = data.find(
    (tier) => userAge >= tier.ageRange.min && userAge <= tier.ageRange.max
  );

  if (!ageTier) {
    return { subscriptionPrice: null, promoPrice: null, projectedPrice: null };
  }

  const pricingDetails = ageTier[locationSize];

  if (!pricingDetails || typeof pricingDetails.regular !== 'number') {
    return { subscriptionPrice: null, promoPrice: null, projectedPrice: null };
  }

  const regularPrice = pricingDetails.regular;
  const currentPromoPrice = typeof pricingDetails.promo === 'number' ? pricingDetails.promo : null;

  // Calculate 2026 projected monthly price (20% increase on base monthly)
  const baseMonthlyPrice = regularPrice / 12;
  const projectedIncrease = baseMonthlyPrice * 0.20; // 20% increase
  const calculatedProjectedPrice = parseFloat((baseMonthlyPrice + projectedIncrease).toFixed(2));

  return {
    subscriptionPrice: regularPrice,
    promoPrice: currentPromoPrice,
    projectedPrice: calculatedProjectedPrice,
  };
};